/**
 * Configuration for post-login redirect paths
 * 
 * This module defines which paths are allowed for redirect after BankID authentication.
 * When a user visits a specific page while not authenticated, the path is stored in sessionStorage.
 * After successful login, the user is redirected to that path if it's in the allowed list.
 */

/**
 * List of path patterns that are allowed for post-login redirect
 * Paths starting with these patterns will be preserved and redirected to after login
 */
export const ALLOWED_REDIRECT_PATH_PREFIXES = [
  '/mitt-konto/',
  '/transaktioner/',
  '/kort',
  '/ersattningskort',
  '/nyttkort',
  '/avsluta-konto',
  '/kontakt',
] as const;

/**
 * Paths that should NOT be stored for redirect (login/auth related paths)
 */
export const EXCLUDED_PATHS = [
  '/',
  '/login',
  '/loggain',
  '/callback',
  '/loggaut',
] as const;

/**
 * Default redirect path when no stored path exists or stored path is not allowed
 */
export const DEFAULT_REDIRECT_PATH = '/mitt-konto/dashboard';

/**
 * SessionStorage keys for path preservation
 */
export const STORAGE_KEYS = {
  ORIGINAL_PATH: 'qstar_original_path',
  REDIRECT_PROCESSED: 'qstar_redirect_processed',
  SCROLL_TO_HASH: 'qstar_scroll_to_hash',
} as const;

/**
 * Check if a path should be excluded from being stored for redirect
 */
export const shouldExcludePath = (pathname: string): boolean => {
  return EXCLUDED_PATHS.some(excluded => 
    pathname === excluded || pathname.startsWith(excluded + '/')
  );
};

/**
 * Check if a stored path is allowed for redirect
 */
export const isAllowedRedirectPath = (pathname: string): boolean => {
  return ALLOWED_REDIRECT_PATH_PREFIXES.some(prefix => 
    pathname.startsWith(prefix)
  );
};

/**
 * Get the redirect path after successful login
 * Returns the stored path if valid, otherwise returns the default path
 * NOTE: Does NOT clear the stored path - caller must clear it after successful navigation
 */
export const getPostLoginRedirectPath = (): string => {
  const storedPath = sessionStorage.getItem(STORAGE_KEYS.ORIGINAL_PATH);
  
  if (!storedPath) {
    return DEFAULT_REDIRECT_PATH;
  }
  
  // Parse the stored path to get pathname only (without search and hash)
  const url = new URL(storedPath, window.location.origin);
  const pathname = url.pathname;
  
  // Check if it's an excluded path (root, login, etc.)
  if (shouldExcludePath(pathname)) {
    return DEFAULT_REDIRECT_PATH;
  }
  
  // Check if it's an allowed redirect path
  if (!isAllowedRedirectPath(pathname)) {
    return DEFAULT_REDIRECT_PATH;
  }
  
  // Extract hash for later scrolling
  if (url.hash) {
    const hashWithoutSymbol = url.hash.substring(1); // Remove '#'
    sessionStorage.setItem(STORAGE_KEYS.SCROLL_TO_HASH, hashWithoutSymbol);
  }
  
  return storedPath;
};

/**
 * Clear the stored redirect path
 * Should be called after successful navigation to prevent re-redirects
 */
export const clearStoredRedirectPath = (): void => {
  sessionStorage.removeItem(STORAGE_KEYS.ORIGINAL_PATH);
};

/**
 * Store the current path for post-login redirect
 * Should be called when a non-authenticated user visits a page
 */
export const storePathForRedirect = (): void => {
  const pathname = window.location.pathname;
  
  // Don't store if it's an excluded path
  if (shouldExcludePath(pathname)) {
    return;
  }
  
  // Don't store if it's not an allowed path
  if (!isAllowedRedirectPath(pathname)) {
    return;
  }
  
  // Store the full path including search and hash
  const fullPath = pathname + window.location.search + window.location.hash;
  sessionStorage.setItem(STORAGE_KEYS.ORIGINAL_PATH, fullPath);
};
